import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet-draw"

// Corrige os ícones padrão do Leaflet (problema conhecido com bundlers)
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png"
import iconUrl from "leaflet/dist/images/marker-icon.png"
import shadowUrl from "leaflet/dist/images/marker-shadow.png"

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl
})

/**
 * Controller Stimulus para mapas interativos com Leaflet e Leaflet-draw
 * 
 * Conecta-se automaticamente via MapInput do Formtastic
 * Suporta desenho, edição e exclusão de geometrias
 * Salva dados nos campos geometry
 */
export default class extends Controller {
  static targets = [
    "mapContainer", 
    "geometryInput",
    "geometryTypeInput",
    "infoText",
    "cepInput",
    "searchButton",
    "locationButton"
  ]

  static values = {
    readonly: Boolean,
    centerLat: { type: Number, default: -8.0476 },
    centerLng: { type: Number, default: -34.8770 },
    zoom: { type: Number, default: 12 }
  }

  connect() {
    // Verifica se já foi inicializado para evitar duplicação
    if (this.map) {
      // Se o mapa já existe, força redimensionamento
      setTimeout(() => {
        this.map.invalidateSize()
        this.map.fitBounds(this.drawnItems.getBounds(), { padding: [20, 20] })
      }, 100)
      return
    }
    
    // Inicializa variáveis de controle
    this.currentRouteType = 'main' // 'main' ou 'segment'
    this.routeColors = {
      main: 'var(--menu-color, #D12E5E)', // Cor do menu para percurso principal
      segment: '#FF530E' // Laranja para segmentos
    }
    
    if (this.hasGeometryInputTarget) {
      this.initializeMap()
      // Inicializa ferramentas de desenho se não for readonly
      if (!this.readonlyValue) {
        this.initializeDrawTools()
      }
      this.loadExistingGeometry()
      this.setupEventListeners()
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
    }
  }

  /**
   * Força redimensionamento do mapa quando necessário
   */
  resizeMap() {
    if (this.map) {
      setTimeout(() => {
        this.map.invalidateSize()
        if (this.drawnItems && this.drawnItems.getLayers().length > 0) {
          this.map.fitBounds(this.drawnItems.getBounds(), { 
            padding: [20, 20],
            maxZoom: 16
          })
        }
      }, 200)
    }
  }

  /**
   * Configura listener para mudanças de visibilidade
   */
  setupVisibilityListener() {
    // Observer para mudanças no container do mapa
    const mapContainer = this.mapContainerTarget
    
    // Observer para mudanças de visibilidade
    const observer = new MutationObserver(() => {
      if (mapContainer.offsetWidth > 0 && mapContainer.offsetHeight > 0) {
        this.map.invalidateSize()
      }
    })
    
    // Observer para mudanças de atributos e childList
    observer.observe(mapContainer, {
      attributes: true,
      childList: true,
      subtree: true
    })
    
    // Listener para mudanças de visibilidade
    const visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(() => this.map.invalidateSize(), 100)
        }
      })
    })
    
    visibilityObserver.observe(mapContainer)
    
    // Listener para redimensionamento da janela
    window.addEventListener('resize', () => {
      setTimeout(() => this.map.invalidateSize(), 100)
    })
  }

  /**
   * Cria ícone customizado para polylines
   */
  createCustomPolylineIcon(color) {
    // Usa a cor fornecida ou a cor padrão do menu
    const fillColor = color || 'var(--menu-color, #D12E5E)';
    
    // Se for um segmento, usa o ícone personalizado para segmentos
    if (color === this.routeColors.segment) {
      return new L.DivIcon({
        html: `<div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
                 <svg width="32" height="32" viewBox="0 0 61 61" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <circle cx="30.4172" cy="30.0833" r="30" fill="${fillColor}"/>
                   <path d="M18.7505 45.0833H24.5839M21.6672 45.0833V15.9167" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                   <path d="M42.0838 17.375H21.6672V29.0417H42.0838L39.1672 23.2083L42.0838 17.375Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                 </svg>
               </div>`,
        className: 'custom-polyline-icon segment-start-icon',
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });
    }
    
    // Ícone padrão para percurso principal
    return new L.DivIcon({
      html: `<div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
               <svg width="32" height="32" viewBox="0 0 61 61" fill="none" xmlns="http://www.w3.org/2000/svg">
                 <circle cx="30.4172" cy="30.6667" r="30" fill="${fillColor}"/>
                 <path d="M34.9172 12.6667C36.5672 12.6667 37.9172 14.0167 37.9172 15.6667C37.9172 17.3167 36.5672 18.6667 34.9172 18.6667C33.2672 18.6667 31.9172 17.3167 31.9172 15.6667C31.9172 14.0167 33.2672 12.6667 34.9172 12.6667ZM42.4172 30.6667C40.3172 30.6667 36.3872 29.0467 33.6872 25.3867L32.4572 32.4367L36.4172 36.7567V48.6667H33.4172V37.9267L30.0872 34.2967L28.5272 42.2167L17.2172 39.9067L17.8172 36.9667L26.1572 38.6767L29.0372 24.0067L24.4172 25.7167V30.6667H21.4172V23.6167L31.2572 19.9867C32.7272 19.4467 34.3472 20.1667 35.0372 21.5767C37.5272 26.6767 41.1872 27.6667 42.4172 27.6667V30.6667Z" fill="white"/>
               </svg>
             </div>`,
      className: 'custom-polyline-icon',
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    })
  }

  /**
   * Cria ícone customizado para saúde
   */
  createHealthIcon() {
    return new L.DivIcon({
      html: `<div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
               <svg width="32" height="32" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                 <path d="M59.08 122.344L57.544 121.304C49.8567 115.925 42.7134 109.808 36.216 103.04C24.8 91.08 12 73.152 12 51.992C12 25.96 33.128 0 64 0C94.872 0 116 25.96 116 52C116 73.16 103.2 91.088 91.784 103.032C85.2866 109.8 78.1433 115.917 70.456 121.296C69.8053 121.749 69.2933 122.096 68.92 122.336C67.296 123.416 65.64 124.44 64 125.48C62.36 124.44 60.704 123.416 59.08 122.344Z" fill="#dc3545"/>
                 <path d="M57.6667 30L52.3333 35.3333V40.6667H44.3333C41.2667 40.6667 39.32 43.3333 39 46L36.3333 72.6667C36.0133 75.3333 37.7733 78 41.6667 78H84.3333C88.2267 78 89.9867 75.3333 89.6667 72.6667L87 46C86.68 43.3333 84.4933 40.6667 81.6667 40.6667H73.6667V35.3333L68.3333 30H57.6667ZM57.6667 35.3333H68.3333V40.6667H57.6667V35.3333ZM60.3333 48.6667H65.6667V56.6667H73.6667V62H65.6667V70H60.3333V62H52.3333V56.6667H60.3333V48.6667Z" fill="white"/>
               </svg>
             </div>`,
      className: 'custom-health-icon',
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    })
  }

  /**
   * Cria ícone customizado para hidratação
   */
  createHydrationIcon() {
    return new L.DivIcon({
      html: `<div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
               <svg width="32" height="32" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                 <path d="M59.08 122.344L57.544 121.304C49.8567 115.925 42.7134 109.808 36.216 103.04C24.8 91.08 12 73.152 12 51.992C12 25.96 33.128 0 64 0C94.872 0 116 25.96 116 52C116 73.16 103.2 91.088 91.784 103.032C85.2866 109.8 78.1433 115.917 70.456 121.296C69.8053 121.749 69.2933 122.096 68.92 122.336C67.296 123.416 65.64 124.44 64 125.48C62.36 124.44 60.704 123.416 59.08 122.344Z" fill="#4784FF"/>
                 <path d="M64 30.1231C64 30.1231 43.9196 52.1976 43.9196 67.8157C43.9196 80.1373 51.6784 87.8961 64 87.8961C76.3216 87.8961 84.0804 80.1373 84.0804 67.8157C84.0804 52.1976 64 30.1231 64 30.1231ZM66.2312 80.6449V77.2981C68.4494 77.2955 70.576 76.4132 72.1446 74.8447C73.7131 73.2762 74.5954 71.1495 74.598 68.9313H77.9447C77.9414 72.0369 76.7062 75.0144 74.5102 77.2104C72.3142 79.4064 69.3368 80.6415 66.2312 80.6449Z" fill="white"/>
               </svg>
             </div>`,
      className: 'custom-hydration-icon',
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    })
  }

  /**
   * Cria ícone personalizado para a linha de chegada
   */
  createFinishLineIcon(color) {
    // Usa a cor fornecida ou a cor padrão do menu
    const fillColor = color || 'var(--menu-color, #D12E5E)';
    
    // Se for um segmento, usa o ícone personalizado para chegada de segmentos
    if (color === this.routeColors.segment) {
      return new L.DivIcon({
        html: `<div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
                 <svg width="32" height="32" viewBox="0 0 61 61" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <circle cx="30.4172" cy="30.0833" r="30" fill="${fillColor}"/>
                   <path d="M14.5838 17.625L25.6672 12.75L35.1672 17.625L45.1469 13.2359C45.2674 13.1829 45.3988 13.1614 45.5293 13.1734C45.6599 13.1854 45.7855 13.2304 45.8949 13.3045C46.0043 13.3786 46.0941 13.4794 46.1561 13.5979C46.2182 13.7164 46.2506 13.8489 46.2505 13.9834V40.375L35.1672 45.25L25.6672 40.375L15.6874 44.7641C15.5669 44.8171 15.4355 44.8386 15.305 44.8266C15.1744 44.8146 15.0488 44.7696 14.9394 44.6955C14.83 44.6214 14.7403 44.5206 14.6782 44.4021C14.6161 44.2836 14.5837 44.1511 14.5838 44.0166V17.625Z" fill="white"/>
                   <g clip-path="url(#clip0_712_1033)">
                     <path d="M40.1564 23.9375L38.7814 22.5625L35.7189 25.6875L32.6564 22.5625L31.2814 23.9375L34.4064 27L31.2814 30.0625L32.6564 31.4375L35.7189 28.3125L38.7814 31.4375L40.1564 30.0625L37.0314 27L40.1564 23.9375Z" fill="${fillColor}"/>
                   </g>
                   <path fill-rule="evenodd" clip-rule="evenodd" d="M19.8128 35.7494L19.3656 35.266C18.8968 34.6472 18.5228 33.8975 18.374 33.1119L19.6938 32.8317C19.8425 33.6173 20.2545 34.3146 20.8061 34.8741L19.8128 35.7494ZM18.3359 31.5683L18.4039 30.9795C18.5276 30.5191 18.6709 30.0641 18.8334 29.6158L19.3047 28.8008L20.4785 29.4918C20.2014 29.9291 19.9506 30.3855 19.8235 30.8913C19.705 31.1645 19.7033 31.4424 19.6755 31.7013L18.3359 31.5683ZM21.1752 28.6405L20.3376 27.5951C20.7903 27.2453 21.2358 26.8505 21.7818 26.6478C22.1398 26.4285 22.5123 26.2996 22.8848 26.1707L23.3 27.4687C22.7736 27.6455 22.2541 27.867 21.7749 28.198L21.1752 28.6405ZM24.4279 27.2888L24.4181 25.926C25.3607 25.891 26.3393 26.0816 27.2348 26.3317L26.9333 27.6284C26.0898 27.4164 25.2729 27.2232 24.4279 27.2888ZM28.2562 27.9491L28.5317 26.6333C29.2702 26.7696 30.0656 26.8274 30.8035 26.5245L31.2831 27.7891C30.8277 27.9776 30.3768 28.0496 29.8734 28.0837C29.3311 28.0969 28.7889 28.0518 28.2562 27.9491Z" fill="${fillColor}"/>
                   <defs>
                     <clipPath id="clip0_712_1033">
                       <rect width="10" height="10" fill="white" transform="translate(30.7189 22)"/>
                     </clipPath>
                   </defs>
                 </svg>
               </div>`,
        className: 'custom-finishline-icon segment-finish-icon',
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });
    }
    
    // Ícone padrão para percurso principal
    return new L.DivIcon({
      html: `<div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
               <svg width="32" height="32" viewBox="0 0 61 61" fill="none" xmlns="http://www.w3.org/2000/svg">
                 <circle cx="30.4172" cy="30.6667" r="30" fill="${fillColor}"/>
                 <path d="M39.9172 14.8334V19.5834H46.2505C46.2072 23.4051 45.6068 26.1003 44.4491 27.669L44.2714 27.8959C43.3407 29.0127 41.752 29.8066 39.5054 30.2774C38.4671 33.6774 35.5731 36.268 32.0039 36.8681C32.0009 36.9109 32.0005 36.9554 32.0005 37C32.0005 40.4978 34.836 43.3334 38.3339 43.3334V46.5H22.5005V43.3334C25.9983 43.3334 28.8339 40.4978 28.8339 37L28.8304 36.8681C25.2611 36.268 22.3673 33.6774 21.3295 30.2771C19.0824 29.8065 17.4936 29.0128 16.563 27.8959C15.289 26.367 14.6293 23.5962 14.5839 19.5834H20.9172V14.8334H39.9172ZM30.4172 18L28.5869 22.351L24.0839 22.8387L27.4595 26.0054L26.5032 30.6667L30.4172 28.2727L34.3312 30.6667L33.3749 26.0117L36.7505 22.845L32.2475 22.3574L30.4172 18ZM20.9172 22.75H17.969L18.0033 22.978C18.2064 24.2777 18.518 25.1999 18.8921 25.7329L18.9957 25.8686C19.2878 26.2192 19.837 26.5494 20.6506 26.8268L20.9172 26.911V22.75ZM42.8654 22.75H39.9172V26.911L40.1838 26.8268C40.9974 26.5494 41.5464 26.2192 41.8386 25.8686L41.9422 25.7329C42.3164 25.1999 42.6279 24.2777 42.8309 22.978L42.8654 22.75Z" fill="white"/>
               </svg>
             </div>`,
      className: 'custom-finishline-icon',
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    })
  }

  /**
   * Inicializa o mapa do Leaflet
   */
  initializeMap() {
    this.map = L.map(this.mapContainerTarget, {
      center: [this.centerLatValue, this.centerLngValue],
      zoom: this.zoomValue,
      zoomControl: false // Remove o controle de zoom padrão
    })

    // L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    //   attribution: '© OpenStreetMap contributors'
    // }).addTo(this.map)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a>'
    }).addTo(this.map)

    // Adiciona controle de zoom no lado direito
    L.control.zoom({
      position: 'topright'
    }).addTo(this.map)

    this.drawnItems = new L.FeatureGroup()
    this.map.addLayer(this.drawnItems)

    // Força redimensionamento após renderização
    setTimeout(() => {
      this.map.invalidateSize()
      // Adiciona listener para mudanças de visibilidade
      this.setupVisibilityListener()
    }, 100)
  }

  setupEventListeners() {
    this.map.on(L.Draw.Event.CREATED, (e) => {
      this.onGeometryCreated(e)
      this.updateInfo()
    })

    this.map.on(L.Draw.Event.EDITED, () => {
      this.saveGeometryData()
      this.updateInfo()
    })

    this.map.on(L.Draw.Event.DELETED, () => {
      this.saveGeometryData()
      this.updateInfo()
    })

    this.drawnItems.on('click', this.onLayerClick.bind(this))
  }

  /**
   * Inicializa as ferramentas de desenho
   */
  initializeDrawTools() {
    // Configuração de idioma para o leaflet-draw
    if (L.drawLocal) {
      // Inicializa a estrutura se não existir
      if (!L.drawLocal.draw) L.drawLocal.draw = {}
      if (!L.drawLocal.draw.toolbar) L.drawLocal.draw.toolbar = {}
      if (!L.drawLocal.draw.handlers) L.drawLocal.draw.handlers = {}
      if (!L.drawLocal.draw.handlers.polyline) L.drawLocal.draw.handlers.polyline = {}
      if (!L.drawLocal.draw.handlers.polyline.tooltip) L.drawLocal.draw.handlers.polyline.tooltip = {}
      
      if (!L.drawLocal.edit) L.drawLocal.edit = {}
      if (!L.drawLocal.edit.toolbar) L.drawLocal.edit.toolbar = {}
      if (!L.drawLocal.edit.toolbar.buttons) L.drawLocal.edit.toolbar.buttons = {}
      if (!L.drawLocal.edit.handlers) L.drawLocal.edit.handlers = {}
      if (!L.drawLocal.edit.handlers.edit) L.drawLocal.edit.handlers.edit = {}
      if (!L.drawLocal.edit.handlers.edit.tooltip) L.drawLocal.edit.handlers.edit.tooltip = {}
      if (!L.drawLocal.edit.handlers.remove) L.drawLocal.edit.handlers.remove = {}
      
      // Configuração de idioma para o leaflet-draw
      L.drawLocal.draw.toolbar.undo = L.drawLocal.draw.toolbar.undo || {}
      L.drawLocal.draw.toolbar.finish = L.drawLocal.draw.toolbar.finish || {}
      L.drawLocal.draw.toolbar.actions = L.drawLocal.draw.toolbar.actions || {}
      
      L.drawLocal.draw.toolbar.undo.text = "Apagar último ponto"
      L.drawLocal.draw.toolbar.finish.text = "Finalizar"
      L.drawLocal.draw.toolbar.actions.text = "Cancelar"
      L.drawLocal.draw.toolbar.actions.title = "Cancelar desenho"
      
      // Textos durante o desenho de linha
      L.drawLocal.draw.handlers.polyline.tooltip.start = "Clique para começar a desenhar a linha"
      L.drawLocal.draw.handlers.polyline.tooltip.cont = "Clique para continuar desenhando a linha"
      L.drawLocal.draw.handlers.polyline.tooltip.end = "Clique no último ponto para finalizar a linha"
      
      // Textos para edição de camadas
      L.drawLocal.edit.toolbar.buttons.edit = "Editar camadas"
      L.drawLocal.edit.toolbar.buttons.editDisabled = "Nenhuma camada para editar"
      L.drawLocal.edit.toolbar.undo = L.drawLocal.edit.toolbar.undo || {}
      L.drawLocal.edit.toolbar.undo.text = "Apagar último ponto"
      L.drawLocal.edit.toolbar.undo.title = "Apagar último ponto"
      // Traduções específicas para os botões de confirmação
      L.drawLocal.edit.toolbar.buttons.save = "Salvar"
      L.drawLocal.edit.toolbar.buttons.cancel = "Cancelar"
      
      // Textos durante a edição
      L.drawLocal.edit.handlers.edit.tooltip.text = "Arraste os pontos ou marcadores para editar as formas"
      L.drawLocal.edit.handlers.edit.tooltip.subtext = "Clique em cancelar para desfazer as alterações"
      L.drawLocal.edit.handlers.remove.tooltip = L.drawLocal.edit.handlers.remove.tooltip || {}
      L.drawLocal.edit.handlers.remove.tooltip.text = "Clique duas vezes em uma forma para removê-la"
    }
    
    this.drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polyline: {
          shapeOptions: {
            color: this.routeColors[this.currentRouteType],
            weight: 6
          }
        },
        polygon: false,
        circle: false,
        rectangle: false,
        circlemarker: false,
        marker: false
      },
      edit: {
        featureGroup: this.drawnItems,
        remove: true
      }
    })
    
    this.map.addControl(this.drawControl)
    
    // Adiciona botões customizados para saúde e hidratação após um delay
    // para garantir que o controle de desenho esteja completamente inicializado
    setTimeout(() => {
      this.addCustomMarkerButtons()
      this.updatePolylineButtonTitle()
      this.forceTranslations()
    }, 200)
  }
  
  /**
   * Adiciona botões customizados para saúde e hidratação
   */
  addCustomMarkerButtons() {
    // Aguarda um pouco para garantir que o toolbar esteja criado
    setTimeout(() => {
      const toolbar = document.querySelector('.leaflet-draw-toolbar-top')
      if (!toolbar) {
        setTimeout(() => this.addCustomMarkerButtons(), 500)
        return
      }
      
      // Remove botões existentes para evitar duplicação
      const existingHealth = toolbar.querySelector('.leaflet-draw-draw-marker-health')
      const existingHydration = toolbar.querySelector('.leaflet-draw-draw-marker-hydration')
      
      if (existingHealth) existingHealth.remove()
      if (existingHydration) existingHydration.remove()
      
      // Botão de Saúde
      const healthButton = document.createElement('a')
      healthButton.href = '#'
      healthButton.className = 'leaflet-draw-draw-marker-health'
      healthButton.title = 'Adicionar posto médico'
      healthButton.innerHTML = '<span class="sr-only">Adicionar posto médico</span>'
      healthButton.style.cssText = `
        background-image: url("data:image/svg+xml,%3Csvg width='200' height='200' viewBox='0 0 200 200' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M90.1042 46.875L81.7708 55.2083V63.5417H69.2708C64.4792 63.5417 61.4375 67.7083 60.9375 71.875L56.7708 113.5417C56.2708 117.7083 59.0625 121.875 65.1042 121.875H131.7708C137.8125 121.875 140.6042 117.7083 140.1042 113.5417L135.9375 71.875C135.4375 67.7083 132.1458 63.5417 127.6042 63.5417H115.1042V55.2083L106.7708 46.875H90.1042ZM90.1042 55.2083H106.7708V63.5417H90.1042V55.2083ZM94.2708 76.0417H102.6042V88.5417H115.1042V96.875H102.6042V109.375H94.2708V96.875H81.7708V88.5417H94.2708V76.0417Z' fill='%23dc3545'/%3E%3C/svg%3E");
        background-size: 30px 30px;
        background-position: center;
        background-repeat: no-repeat;
        display: block;
        width: 32px;
        height: 32px;
        margin: 1px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: #fff;
        cursor: pointer;
      `
      healthButton.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.startCustomMarkerMode('health')
      })
      
      // Botão de Hidratação
      const hydrationButton = document.createElement('a')
      hydrationButton.href = '#'
      hydrationButton.className = 'leaflet-draw-draw-marker-hydration'
      hydrationButton.title = 'Adicionar ponto de hidratação'
      hydrationButton.innerHTML = '<span class="sr-only">Adicionar ponto de hidratação</span>'
      hydrationButton.style.cssText = `
        background-image: url("data:image/svg+xml,%3Csvg width='128' height='128' viewBox='0 0 128 128' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M64 30.1231C64 30.1231 43.9196 52.1976 43.9196 67.8157C43.9196 80.1373 51.6784 87.8961 64 87.8961C76.3216 87.8961 84.0804 80.1373 84.0804 67.8157C84.0804 52.1976 64 30.1231 64 30.1231ZM66.2312 80.6449V77.2981C68.4494 77.2955 70.576 76.4132 72.1446 74.8447C73.7131 73.2762 74.5954 71.1495 74.598 68.9313H77.9447C77.9414 72.0369 76.7062 75.0144 74.5102 77.2104C72.3142 79.4064 69.3368 80.6415 66.2312 80.6449Z' fill='%234784FF'/%3E%3C/svg%3E);
        background-size: 30px 30px;
        background-position: center;
        background-repeat: no-repeat;
        display: block;
        width: 32px;
        height: 32px;
        margin: 1px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: #fff;
        cursor: pointer;
      `
      hydrationButton.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.startCustomMarkerMode('hydration')
      })
      
      // Botão de Segmento
      const segmentButton = document.createElement('a')
      segmentButton.href = '#'
      segmentButton.className = 'leaflet-draw-draw-segment'
      segmentButton.title = 'Desenhar segmento'
      segmentButton.innerHTML = '<span class="sr-only">Desenhar segmento</span>'
      segmentButton.style.cssText = `
        background-image: url("data:image/svg+xml,%3Csvg width='47' height='47' viewBox='0 0 47 47' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M29.4689 10.2497L41.5499 5.07291C41.6957 5.01049 41.8549 4.98554 42.0128 4.99966C42.1708 5.01383 42.3228 5.06661 42.4552 5.15396C42.5875 5.24131 42.6965 5.36002 42.7716 5.49966C42.8467 5.63941 42.886 5.79608 42.8859 5.95474V37.0836L29.4689 42.8336L17.9689 37.0836L5.88782 42.2604C5.74207 42.3228 5.58285 42.3478 5.42493 42.3336C5.267 42.3195 5.11489 42.2667 4.98254 42.1794C4.8502 42.092 4.74125 41.9733 4.66614 41.8336C4.59101 41.6939 4.55174 41.5372 4.55188 41.3786V10.2497L17.9689 4.49966L29.4689 10.2497ZM10.1407 29.7672C10.3209 30.6938 10.7734 31.5784 11.3409 32.3083L11.882 32.8786L13.0841 31.8454C12.4166 31.1856 11.9176 30.3635 11.7374 29.4372L10.1407 29.7672ZM10.6964 25.6432C10.4997 26.1717 10.3266 26.7087 10.1769 27.2516L10.0939 27.9469L11.7159 28.1032C11.7496 27.7979 11.7514 27.4702 11.8947 27.1481C12.0485 26.5516 12.3523 26.0134 12.6876 25.4977L11.2667 24.6823L10.6964 25.6432ZM31.2189 21.7233L27.8497 18.2858L26.338 19.7975L29.7755 23.1667L26.338 26.5358L27.8497 28.0475L31.2189 24.61L34.588 28.0475L36.0997 26.5358L32.6622 23.1667L36.0997 19.7975L34.588 18.2858L31.2189 21.7233ZM15.6007 21.5797C15.1498 21.7318 14.6992 21.8846 14.2657 22.1432C13.6048 22.3823 13.0656 22.8478 12.5177 23.2604L13.5314 24.4928L14.257 23.9713C14.837 23.5809 15.4664 23.3195 16.1036 23.111L15.6007 21.5797ZM25.1866 21.9977C24.2934 22.355 23.3306 22.2864 22.4366 22.1256L22.1027 23.6774C22.7475 23.7985 23.4042 23.8522 24.0607 23.8366C24.6699 23.7964 25.2156 23.7111 25.7667 23.4889L25.1866 21.9977ZM17.4572 21.2917L17.4689 22.8991C18.4917 22.8218 19.4811 23.0494 20.5021 23.2995L20.8663 21.7702C19.7825 21.4753 18.598 21.2504 17.4572 21.2917Z' fill='%23FF530E'/%3E%3C/svg%3E");
        background-size: 24px 24px;
        background-position: center;
        background-repeat: no-repeat;
        display: block;
        width: 32px;
        height: 32px;
        margin: 1px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: #fff;
        cursor: pointer;
      `
      segmentButton.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.startSegmentDrawMode()
      })
      
      toolbar.appendChild(healthButton)
      // Adiciona o botão de segmento após o botão de polyline
      const polylineButton = document.querySelector('.leaflet-draw-draw-polyline')
      if (polylineButton && polylineButton.parentNode) {
        polylineButton.parentNode.insertBefore(segmentButton, polylineButton.nextSibling)
      } else {
        // Fallback: adiciona ao final da toolbar se não encontrar o botão de polyline
        toolbar.appendChild(segmentButton)
      }
      toolbar.appendChild(hydrationButton)
    }, 100)
  }
  
  /**
   * Atualiza o título do botão de polyline
   */
  updatePolylineButtonTitle() {
    setTimeout(() => {
      const polylineButton = document.querySelector('.leaflet-draw-draw-polyline')
      if (polylineButton) {
        polylineButton.title = 'Desenhar percurso principal'
        polylineButton.setAttribute('data-bs-original-title', 'Desenhar percurso principal')
      }
    }, 300)
  }
  
  /**
   * Inicia modo de desenho de segmento
   */
  startSegmentDrawMode() {
    // Muda para modo segmento
    this.currentRouteType = 'segment'
    
    // Armazena o tipo atual para restaurar depois
    this.previousRouteType = 'main'
    
    // Configura a cor para o segmento sem recriar o controle
    if (this.drawControl) {
      // Apenas modifica as opções de cor sem recriar o controle
      if (this.drawControl.options && this.drawControl.options.draw && this.drawControl.options.draw.polyline) {
        this.drawControl.options.draw.polyline.shapeOptions.color = this.routeColors.segment;
      }
    }
    
    // Simula clique no botão de polyline para iniciar o desenho
    const polylineButton = document.querySelector('.leaflet-draw-draw-polyline')
    if (polylineButton) {
      polylineButton.click()
    }
  }
  
  /**
   * Atualiza a cor do controle de desenho
   */
  updateDrawControlColor() {
    // Armazena os ícones personalizados antes de remover o controle
    const customIcons = Array.from(document.querySelectorAll('.leaflet-draw-toolbar-custom-button'));
    
    // Armazena as camadas atuais e seu estado de visibilidade
    const currentLayers = [];
    if (this.drawnItems) {
      this.drawnItems.eachLayer(layer => {
        currentLayers.push({
          layer: layer,
          isVisible: this.map.hasLayer(layer),
          properties: layer.feature ? JSON.parse(JSON.stringify(layer.feature)) : null
        });
      });
    }
    
    // Remove o controle atual
    if (this.drawControl) {
      this.map.removeControl(this.drawControl)
    }
    
    // Recria o controle com a nova cor
    this.drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polyline: {
          shapeOptions: {
            color: this.routeColors[this.currentRouteType],
            weight: 6
          }
        },
        polygon: false,
        circle: false,
        rectangle: false,
        circlemarker: false,
        marker: false
      },
      edit: {
        featureGroup: this.drawnItems,
        remove: true
      }
    })
    
    this.map.addControl(this.drawControl)
    
    // Restaura os ícones personalizados após recriar o controle
    setTimeout(() => {
      const toolbar = document.querySelector('.leaflet-draw-toolbar');
      if (toolbar && customIcons.length > 0) {
        customIcons.forEach(icon => {
          toolbar.appendChild(icon);
        });
      }
      
      // Garante que todas as camadas anteriores permaneçam visíveis com suas propriedades
      currentLayers.forEach(item => {
        if (item.isVisible && !this.map.hasLayer(item.layer)) {
          this.drawnItems.addLayer(item.layer);
          
          // Restaura as propriedades da feature se existirem
          if (item.properties) {
            item.layer.feature = item.properties;
          }
        }
      });
    }, 50)
  }
  
  /**
   * Força as traduções após a inicialização
   */
  forceTranslations() {
    // Força traduções dos botões de edição
    if (L.drawLocal && L.drawLocal.edit) {
      L.drawLocal.edit.toolbar.buttons.save = "Salvar"
      L.drawLocal.edit.toolbar.buttons.cancel = "Cancelar"
    }
    
    // Também tenta traduzir diretamente os elementos DOM
    setTimeout(() => {
      // Traduz botões Save e Cancel
      const saveButtons = document.querySelectorAll('.leaflet-draw-edit-save')
      const cancelButtons = document.querySelectorAll('.leaflet-draw-edit-cancel')
      
      saveButtons.forEach(button => {
        if (button.textContent === 'Save') {
          button.textContent = 'Salvar'
        }
      })
      
      cancelButtons.forEach(button => {
        if (button.textContent === 'Cancel') {
          button.textContent = 'Cancelar'
        }
      })
      
      // Traduz tooltips dos botões de editar e apagar
      const editButtons = document.querySelectorAll('.leaflet-draw-edit-edit')
      const removeButtons = document.querySelectorAll('.leaflet-draw-edit-remove')
      
      editButtons.forEach(button => {
        if (button.title === 'Edit layers') {
          button.title = 'Editar camadas'
        }
      })
      
      removeButtons.forEach(button => {
        if (button.title === 'Delete layers') {
          button.title = 'Remover camadas'
        }
      })
    }, 500)
  }
  
  /**
   * Inicia modo de criação de marcador customizado
   */
  startCustomMarkerMode(type) {
    const map = this.map
    
    // Remove eventos existentes
    this.exitCustomMarkerMode()
    
    // Variáveis para o modo de criação
    this.customMarkerMode = {
      active: true,
      type: type,
      previewMarker: null
    }
    
    // Cria marcador preview que segue o mouse
    const createPreviewMarker = (latlng) => {
      const icon = type === 'health' ? this.createHealthIcon() : this.createHydrationIcon()
      return L.marker(latlng, { 
        icon,
        opacity: 0.7,
        zIndexOffset: 1000
      })
    }
    
    // Handler para movimento do mouse
    this.customMarkerMouseMoveHandler = (e) => {
      if (!this.customMarkerMode.active) return
      
      if (this.customMarkerMode.previewMarker) {
        this.customMarkerMode.previewMarker.setLatLng(e.latlng)
      } else {
        this.customMarkerMode.previewMarker = createPreviewMarker(e.latlng).addTo(map)
      }
    }
    
    // Handler para clique do mouse
    this.customMarkerClickHandler = (e) => {
      if (!this.customMarkerMode.active) return
      
      // Cria o marcador definitivo
      this.createCustomMarker(e.latlng, type)
      
      // Remove o preview
      if (this.customMarkerMode.previewMarker) {
        map.removeLayer(this.customMarkerMode.previewMarker)
        this.customMarkerMode.previewMarker = null
      }
      
      // Sai do modo
      this.exitCustomMarkerMode()
    }
    
    // Handler para clique direito (cancelar)
    this.customMarkerRightClickHandler = (e) => {
      if (!this.customMarkerMode.active) return
      
      // Remove o preview
      if (this.customMarkerMode.previewMarker) {
        map.removeLayer(this.customMarkerMode.previewMarker)
        this.customMarkerMode.previewMarker = null
      }
      
      // Sai do modo
      this.exitCustomMarkerMode()
    }
    
    // Handler para tecla ESC
    this.customMarkerKeyHandler = (e) => {
      if (e.key === 'Escape' && this.customMarkerMode.active) {
        // Remove o preview
        if (this.customMarkerMode.previewMarker) {
          map.removeLayer(this.customMarkerMode.previewMarker)
          this.customMarkerMode.previewMarker = null
        }
        
        // Sai do modo
        this.exitCustomMarkerMode()
      }
    }
    
    // Adiciona eventos
    map.on('mousemove', this.customMarkerMouseMoveHandler)
    map.on('click', this.customMarkerClickHandler)
    map.on('contextmenu', this.customMarkerRightClickHandler)
    document.addEventListener('keydown', this.customMarkerKeyHandler)
    
    // Muda o cursor
    map.getContainer().style.cursor = 'crosshair'
    
    // Mostra instrução
    if (this.hasInfoTextTarget) {
      this.infoTextTarget.textContent = `Mova o mouse e clique para adicionar ${type === 'health' ? 'posto médico' : 'ponto de hidratação'} (ESC para cancelar)`
    }
  }
  
  /**
   * Sai do modo de criação de marcador customizado
   */
  exitCustomMarkerMode() {
    const map = this.map
    
    // Remove eventos
    if (this.customMarkerMouseMoveHandler) {
      map.off('mousemove', this.customMarkerMouseMoveHandler)
    }
    if (this.customMarkerClickHandler) {
      map.off('click', this.customMarkerClickHandler)
    }
    if (this.customMarkerRightClickHandler) {
      map.off('contextmenu', this.customMarkerRightClickHandler)
    }
    if (this.customMarkerKeyHandler) {
      document.removeEventListener('keydown', this.customMarkerKeyHandler)
    }
    
    // Remove marcador preview se existir
    if (this.customMarkerMode?.previewMarker) {
      map.removeLayer(this.customMarkerMode.previewMarker)
    }
    
    // Limpa modo
    this.customMarkerMode = {
      active: false,
      type: null,
      previewMarker: null
    }
    
    // Restaura cursor
    map.getContainer().style.cursor = ''
    
    // Restaura texto informativo
    this.updateInfo()
  }
  
  /**
   * Cria marcador customizado
   */
  createCustomMarker(latlng, type) {
    const icon = type === 'health' ? this.createHealthIcon() : this.createHydrationIcon()
    const title = type === 'health' ? 'Posto Médico' : 'Hidratação'
    const description = type === 'health' ? 'Ponto de atendimento médico' : 'Ponto de hidratação'
    
    const marker = L.marker(latlng, { icon })
      .addTo(this.drawnItems)
      .bindTooltip(title)
    
    marker.feature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [latlng.lng, latlng.lat]
      },
      properties: {
        title: title,
        description: description,
        markerType: type
      }
    }
    
    this.saveGeometryData()
    this.updateInfo()
  }
  
  /**
   * Carrega geometrias existentes do campo hidden
   */
  loadExistingGeometry() {
    if (!this.hasGeometryInputTarget) {
      return
    }
    
    const geometryData = this.geometryInputTarget.value
    if (!geometryData) {
      return
    }
    
    try {
      const geojson = JSON.parse(geometryData)
      
      // Limpa geometrias existentes antes de carregar novas
      this.drawnItems.clearLayers()
      
      if (geojson.type === 'Feature') {
        this.addFeatureToMap(geojson)
      } else if (geojson.type === 'FeatureCollection') {
        geojson.features.forEach((feature, index) => {
          this.addFeatureToMap(feature)
        })
      }
      
      // Verifica se todos os marcadores têm os ícones corretos
      this.drawnItems.getLayers().forEach(layer => {
        if (layer instanceof L.Marker && layer.feature?.properties?.markerType) {
          const markerType = layer.feature.properties.markerType
          const routeType = layer.feature?.properties?.routeType || 'main'
          const color = this.routeColors[routeType]
          
          if (markerType === 'health') {
            layer.setIcon(this.createHealthIcon())
          } else if (markerType === 'hydration') {
            layer.setIcon(this.createHydrationIcon())
          } else if (markerType === 'start') {
            layer.setIcon(this.createCustomPolylineIcon(color))
          } else if (markerType === 'end') {
            layer.setIcon(this.createFinishLineIcon(color))
          }
        }
      })
      
      // Verifica se todas as polylines têm marcadores de início e fim
      this.ensureAllPolylinesHaveMarkers()
      
      // Remove marcadores duplicados de rota se existirem
      this.removeDuplicateRouteMarkers()
      
      // Ajusta o zoom para mostrar todas as geometrias
      if (this.drawnItems.getLayers().length > 0) {
        this.map.fitBounds(this.drawnItems.getBounds(), { 
          padding: [20, 20],
          maxZoom: 16
        })
      }
    } catch (error) {
      // Erro silencioso
    }
  }

  /**
   * Handler para geometria criada
   */
  onGeometryCreated(e) {
    const layer = e.layer
    
    // Se for uma polyline, mantém a linha e adiciona marcadores de largada e chegada
    if (layer instanceof L.Polyline) {
      const latLngs = layer.getLatLngs()
      
      // Adiciona propriedades da rota incluindo o tipo
      layer.feature = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: latLngs.map(latLng => [latLng.lng, latLng.lat])
        },
        properties: {
          title: this.currentRouteType === 'main' ? 'Percurso Principal' : 'Segmento',
          description: this.currentRouteType === 'main' ? 'Rota principal do evento' : 'Segmento da rota',
          routeType: this.currentRouteType
        }
      }
      
      // Mantém a polyline original
      this.drawnItems.addLayer(layer)
      
      // Adiciona tooltip
      layer.bindTooltip(layer.feature.properties.title)
      
      // Sempre cria marcadores de largada e chegada para cada polyline
      if (latLngs.length >= 2) {
        this.createRouteMarkersForPolyline(layer)
      }
      
      // Volta para o modo principal após desenhar um segmento
      if (this.currentRouteType === 'segment') {
        this.currentRouteType = 'main'
        
        // Restaura a cor para o percurso principal sem recriar o controle
        if (this.drawControl && this.drawControl.options && this.drawControl.options.draw && this.drawControl.options.draw.polyline) {
          this.drawControl.options.draw.polyline.shapeOptions.color = this.routeColors.main;
        }
      }
    } else {
      // Para outros tipos de geometria, mantém o comportamento original
      this.drawnItems.addLayer(layer)
      
      // Adiciona propriedades padrão se não existirem
      if (!layer.feature) {
        layer.feature = {
          type: 'Feature',
          geometry: {
            type: layer instanceof L.Marker ? 'Point' : 'LineString',
            coordinates: layer instanceof L.Marker 
              ? [layer.getLatLng().lng, layer.getLatLng().lat]
              : layer.getLatLngs().map(latLng => [latLng.lng, latLng.lat])
          },
          properties: {
            title: 'Novo elemento',
            description: 'Elemento criado no mapa',
            autoGenerated: false
          }
        }
      }
      
      // Se for um marcador criado via desenho padrão, usa ícone padrão
      if (layer instanceof L.Marker && !layer.feature.properties.markerType) {
        layer.setIcon(new L.Icon.Default({
          shadowUrl: null,
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          className: 'leaflet-marker-icon-no-border'
        }))
      }
    }
    
    this.saveGeometryData()
    this.updateInfo()
  }

  /**
   * Handler para geometria editada
   */
  onGeometryEdited() {
    // Atualiza marcadores de rota para todas as polylines editadas
    const layers = this.drawnItems.getLayers()
    const polylines = layers.filter(layer => layer instanceof L.Polyline)
    
    if (polylines.length > 0) {
      // Para cada polyline, remove seus marcadores específicos e recria
      polylines.forEach(polyline => {
        if (polyline.getLatLngs().length >= 2) {
          // Remove marcadores desta polyline específica
          this.removeMarkersForPolyline(polyline)
          
          // Recria marcadores com as novas posições
          this.createRouteMarkersForPolyline(polyline)
        }
      })
    }
    
    this.saveGeometryData()
    this.updateInfo()
  }

  /**
   * Handler para geometria deletada
   */
  onGeometryDeleted() {
    this.saveGeometryData()
    this.updateInfo()
  }

  /**
   * Handler para clique em uma layer
   */
  onLayerClick(e) {
    const layer = e.layer
    
    // Se estiver no modo de criação de marcador customizado, não abre popup
    if (this.customMarkerMode?.active) {
      return
    }
    
    // Se for readonly, abre popup de visualização
    if (this.readonlyValue) {
      this.openViewPopup(layer)
      return
    }
    
    // Para modo de edição, abre popup de propriedades no clique simples
    this.openPropertiesPopup(layer)
  }



  /**
   * Abre popup para edição de propriedades
   */
  openPropertiesPopup(layer) {
    const properties = layer.feature?.properties || {}
    
    const popupContent = `
      <div class="map-popup-form">
        <div class="form-group mb-2">
          <label class="form-label">Título</label>
          <input type="text" class="form-control form-control-sm" 
            value="${properties.title || ''}" 
            placeholder="Digite um título para o elemento"
            data-property="title">
        </div>
        <div class="form-group mb-3">
          <label class="form-label">Descrição</label>
          <textarea class="form-control form-control-sm" 
            rows="2" 
            placeholder="Digite uma descrição para o elemento"
            data-property="description">${properties.description || ''}</textarea>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-secondary btn-sm" data-action="cancel">Cancelar</button>
          <button type="button" class="btn btn-main btn-sm" data-action="save">Salvar</button>
        </div>
      </div>
    `

    const popup = L.popup({
      minWidth: 300,
      maxWidth: 400,
      className: 'map-properties-popup'
    })
    .setContent(popupContent)
    .setLatLng(this.getLayerCenter(layer))
    .openOn(this.map)

    this.setupPopupHandlers(popup, layer)
  }

  setupPopupHandlers(popup, layer) {
    setTimeout(() => {
      const popupElement = popup.getElement()
      const saveButton = popupElement.querySelector('[data-action="save"]')
      const cancelButton = popupElement.querySelector('[data-action="cancel"]')
      
      if (saveButton) {
        saveButton.addEventListener('click', () => {
          const title = popupElement.querySelector('[data-property="title"]').value.trim()
          const description = popupElement.querySelector('[data-property="description"]').value.trim()
          
          // Atualiza as propriedades da layer
          if (!layer.feature) {
            layer.feature = { type: 'Feature', properties: {} }
          }
          
          layer.feature.properties = { title, description }
          
          // Atualiza o tooltip
          layer.unbindTooltip()
          if (title) {
            layer.bindTooltip(title)
          }
          
          // Fecha o popup e salva
          this.map.closePopup()
          this.saveGeometryData()
        })
      }

      if (cancelButton) {
        cancelButton.addEventListener('click', () => {
          this.map.closePopup()
        })
      }
    }, 100)
  }

  /**
   * Abre popup para visualização de propriedades
   */
  openViewPopup(layer) {
    const { title, description } = layer.feature?.properties || {}
    if (!title && !description) return

    const popupContent = `
      <div class="map-popup-view">
        ${title ? `<h6 class="map-popup-title">${title || 'Sem título'}</h6>` : ''}
        ${description ? `<p class="map-popup-description">${description || 'Sem descrição'}</p>` : ''}
      </div>
    `

    L.popup({
      minWidth: 250,
      maxWidth: 350,
      className: 'map-properties-popup'
    })
    .setContent(popupContent)
    .setLatLng(this.getLayerCenter(layer))
    .openOn(this.map)
  }

  /**
   * Retorna o centro de uma layer
   */
  getLayerCenter(layer) {
    if (layer instanceof L.Marker) return layer.getLatLng()
    if (layer instanceof L.Polyline) return layer.getCenter()
    return layer.getBounds().getCenter()
  }

  /**
   * Salva os dados da geometria nos campos hidden
   */
  saveGeometryData() {
    const layers = this.drawnItems.getLayers()
    
    let geometryData = null

    if (layers.length === 0) {
      geometryData = null
    } else if (layers.length === 1) {
      geometryData = layers[0].toGeoJSON()
    } else {
      // Para múltiplas layers, cria uma FeatureCollection
      const features = []
      
      // Separa polylines e markers
      const polylines = layers.filter(layer => layer instanceof L.Polyline)
      const markers = layers.filter(layer => layer instanceof L.Marker)
      
      // Adiciona polylines primeiro
      polylines.forEach(layer => {
        features.push(layer.toGeoJSON())
      })
      
      // Adiciona todos os marcadores (incluindo largada e chegada)
      markers.forEach(layer => {
        const geojson = layer.toGeoJSON()
        
        // Garante que as propriedades customizadas sejam preservadas
        if (layer.feature?.properties) {
          geojson.properties = {
            ...geojson.properties,
            ...layer.feature.properties
          }
        }
        
        features.push(geojson)
      })
      
      geometryData = {
        type: 'FeatureCollection',
        features: features
      }
    }

    // Atualiza os campos
    if (geometryData) {
      const jsonString = JSON.stringify(geometryData)
      
      if (this.hasGeometryInputTarget) {
        this.geometryInputTarget.value = jsonString
      }
      
      this.updateInfo()
    } else {
      this.clearGeometryData()
    }
  }

  /**
   * Limpa os dados da geometria
   */
  clearGeometryData() {
    if (this.hasGeometryInputTarget) {
      this.geometryInputTarget.value = ''
    }
    
    if (this.hasGeometryTypeInputTarget) {
      this.geometryTypeInputTarget.value = ''
    }
    
    this.updateInfo()
    
    // Dispara evento de mudança
    if (this.hasGeometryInputTarget) {
      this.geometryInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  /**
   * Verifica se já existem marcadores de largada e chegada
   */
  hasRouteMarkers() {
    const layers = this.drawnItems.getLayers()
    return layers.some(layer => 
      layer instanceof L.Marker && 
      layer.feature?.properties?.markerType === 'start'
    ) && layers.some(layer => 
      layer instanceof L.Marker && 
      layer.feature?.properties?.markerType === 'end'
    )
  }

  /**
   * Remove marcadores de largada e chegada existentes (todos)
   */
  removeRouteMarkers() {
    const layers = this.drawnItems.getLayers()
    layers.forEach(layer => {
      if (layer instanceof L.Marker && 
          (layer.feature?.properties?.markerType === 'start' || 
           layer.feature?.properties?.markerType === 'end')) {
        this.drawnItems.removeLayer(layer)
      }
    })
  }
  
  /**
   * Remove marcadores específicos de uma polyline
   */
  removeMarkersForPolyline(polyline) {
    const polylineId = polyline.feature?.properties?.polylineId
    if (!polylineId) return
    
    const layers = this.drawnItems.getLayers()
    layers.forEach(layer => {
      if (layer instanceof L.Marker && 
          layer.feature?.properties?.polylineId === polylineId &&
          (layer.feature?.properties?.markerType === 'start' || 
           layer.feature?.properties?.markerType === 'end')) {
        this.drawnItems.removeLayer(layer)
      }
    })
  }

  /**
   * Garante que todas as polylines tenham marcadores de largada e chegada
   */
  ensureAllPolylinesHaveMarkers() {
    // Primeiro, identifica todas as polylines
    const polylines = []
    const existingMarkers = {}
    
    // Coleta todas as polylines e marcadores existentes
    this.drawnItems.getLayers().forEach(layer => {
      if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
        polylines.push(layer)
      } else if (layer instanceof L.Marker && 
                layer.feature?.properties?.isRoutePoint && 
                layer.feature?.properties?.polylineId) {
        
        const polylineId = layer.feature.properties.polylineId
        const markerType = layer.feature.properties.markerType
        
        if (!existingMarkers[polylineId]) {
          existingMarkers[polylineId] = { start: null, end: null }
        }
        
        if (markerType === 'start') {
          existingMarkers[polylineId].start = layer
        } else if (markerType === 'end') {
          existingMarkers[polylineId].end = layer
        }
      }
    })
    
    // Para cada polyline, verifica se tem marcadores de largada e chegada
    polylines.forEach(polyline => {
      // Pula se não tiver feature ou properties
      if (!polyline.feature || !polyline.feature.properties) return
      
      const polylineId = polyline.feature.properties.polylineId || this.generatePolylineId(polyline)
      
      // Se não tiver marcadores ou estiverem incompletos, cria novos
      if (!existingMarkers[polylineId] || 
          !existingMarkers[polylineId].start || 
          !existingMarkers[polylineId].end) {
        
        // Cria marcadores para esta polyline
        this.createRouteMarkersForPolyline(polyline)
      }
    })
  }

  /**
   * Remove marcadores duplicados de rota
   */
  removeDuplicateRouteMarkers() {
    const markersByPolylineId = {}
    
    // Agrupa marcadores por polylineId
    this.drawnItems.getLayers().forEach(layer => {
      if (layer instanceof L.Marker && 
          layer.feature?.properties?.isRoutePoint && 
          layer.feature?.properties?.polylineId) {
        
        const polylineId = layer.feature.properties.polylineId
        const markerType = layer.feature.properties.markerType
        
        if (!markersByPolylineId[polylineId]) {
          markersByPolylineId[polylineId] = { start: [], end: [] }
        }
        
        if (markerType === 'start') {
          markersByPolylineId[polylineId].start.push(layer)
        } else if (markerType === 'end') {
          markersByPolylineId[polylineId].end.push(layer)
        }
      }
    })
    
    // Remove marcadores duplicados para cada polyline
    Object.keys(markersByPolylineId).forEach(polylineId => {
      const markers = markersByPolylineId[polylineId]
      
      // Mantém apenas o primeiro marcador de início e fim
      if (markers.start.length > 1) {
        for (let i = 1; i < markers.start.length; i++) {
          this.drawnItems.removeLayer(markers.start[i])
        }
      }
      
      if (markers.end.length > 1) {
        for (let i = 1; i < markers.end.length; i++) {
          this.drawnItems.removeLayer(markers.end[i])
        }
      }
    })
  }

  /**
   * Cria marcadores de largada e chegada para uma polyline (método legado)
   */
  createRouteMarkers(polyline) {
    // Redireciona para o novo método
    this.createRouteMarkersForPolyline(polyline)
  }
  
  /**
   * Cria marcadores de largada e chegada específicos para uma polyline
   */
  createRouteMarkersForPolyline(polyline) {
    const latLngs = polyline.getLatLngs()
    if (latLngs.length < 2) return
    
    // Determina o tipo de rota e cor correspondente
    const routeType = polyline.feature?.properties?.routeType || 'main'
    const color = this.routeColors[routeType]
    
    // Cria ícones com a cor correspondente ao tipo de rota
    const startIcon = this.createCustomPolylineIcon(color)
    const finishIcon = this.createFinishLineIcon(color)
    
    // Gera um ID único para esta polyline
    const polylineId = this.generatePolylineId(polyline)
    
    // Marcador de largada
    const startMarker = L.marker(latLngs[0], { icon: startIcon })
      .addTo(this.drawnItems)
      .bindTooltip('Largada', { permanent: false, direction: 'top' })
    
    startMarker.feature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [latLngs[0].lng, latLngs[0].lat]
      },
      properties: {
        title: 'Largada',
        description: 'Ponto inicial da rota',
        markerType: 'start',
        isRoutePoint: true,
        polylineId: polylineId,
        routeType: routeType
      }
    }
    
    // Marcador de chegada
    const endMarker = L.marker(latLngs[latLngs.length - 1], { icon: finishIcon })
      .addTo(this.drawnItems)
      .bindTooltip('Chegada', { permanent: false, direction: 'top' })
    
    endMarker.feature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [latLngs[latLngs.length - 1].lng, latLngs[latLngs.length - 1].lat]
      },
      properties: {
        title: 'Chegada',
        description: 'Ponto final da rota',
        markerType: 'end',
        isRoutePoint: true,
        polylineId: polylineId,
        routeType: routeType
      }
    }
    
    // Associa o ID à polyline
    if (!polyline.feature.properties.polylineId) {
      polyline.feature.properties.polylineId = polylineId
    }
  }
  
  /**
   * Gera um ID único para uma polyline
   */
  generatePolylineId(polyline) {
    const latLngs = polyline.getLatLngs()
    const firstPoint = latLngs[0]
    const lastPoint = latLngs[latLngs.length - 1]
    
    // Cria um ID baseado nas coordenadas e timestamp
    return `polyline_${Math.round(firstPoint.lat * 1000)}_${Math.round(firstPoint.lng * 1000)}_${Math.round(lastPoint.lat * 1000)}_${Math.round(lastPoint.lng * 1000)}_${Date.now()}`
  }

  /**
   * Atualiza o texto informativo
   */
  updateInfo() {
    const layers = this.drawnItems.getLayers()
    
    // Contagem de tipos de geometria
    const markerCount = layers.filter(layer => layer instanceof L.Marker).length
    const polylineCount = layers.filter(layer => layer instanceof L.Polyline).length
    
    // Cálculo de distância total para polylines
    const totalDistance = layers
      .filter(layer => layer instanceof L.Polyline)
      .reduce((total, layer) => {
        const latLngs = layer.getLatLngs()
        let distance = 0
        for (let i = 1; i < latLngs.length; i++) {
          distance += latLngs[i-1].distanceTo(latLngs[i])
        }
        return total + (distance / 1000) // converter para km
      }, 0)

    // Texto informativo detalhado
    let infoText = `${markerCount} pontos | ${polylineCount} linhas | Distância total: ${totalDistance.toFixed(2)} km`
    
    // Adiciona dica sobre edição se não for readonly
    if (!this.readonlyValue && (markerCount > 0 || polylineCount > 0)) {
      infoText += ' | Clique para editar propriedades'
    }
    
    // Verifica se o target existe antes de atualizar
    if (this.hasInfoTextTarget) {
      this.infoTextTarget.textContent = infoText
    }
  }

  /**
   * Action: Limpar geometria
   */
  clearGeometry() {
    this.drawnItems.clearLayers()
    this.clearGeometryData()
  }

  /**
   * Action: Forçar redimensionamento do mapa
   */
  forceResize() {
    this.resizeMap()
  }

  /**
   * Action: Buscar localização por CEP
   */
  searchByCep() {
    const cep = this.cepInputTarget.value.replace(/\D/g, '') // Remove caracteres não numéricos
    
    if (cep.length !== 8) {
      this.showSearchError('CEP deve ter 8 dígitos')
      return
    }

    this.searchButtonTarget.disabled = true
    this.searchButtonTarget.innerHTML = '<i class="zmdi zmdi-refresh zmdi-hc-spin"></i>'

    // Busca dados do CEP via ViaCEP
    fetch(`https://viacep.com.br/ws/${cep}/json/`)
      .then(response => response.json())
      .then(data => {
        if (data.erro) {
          throw new Error('CEP não encontrado')
        }
        
        // Geocodifica o endereço usando Nominatim (OpenStreetMap)
        const address = `${data.logradouro}, ${data.bairro}, ${data.localidade}, ${data.uf}, Brasil`
        this.geocodeAddress(address, data)
      })
      .catch(error => {
        this.showSearchError('Erro ao buscar CEP')
      })
      .finally(() => {
        this.searchButtonTarget.disabled = false
        this.searchButtonTarget.innerHTML = '<i class="zmdi zmdi-search"></i>'
      })
  }

  handleCepKeypress(event) {
    // Previne o comportamento padrão do Enter (submit do form)
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      this.searchByCep()
    }
  }

  /**
   * Geocodifica um endereço e move o mapa
   */
  geocodeAddress(address, cepData) {
    const encodedAddress = encodeURIComponent(address)
    
    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`)
      .then(response => response.json())
      .then(results => {
        if (results.length === 0) {
          throw new Error('Endereço não encontrado')
        }
        
        const result = results[0]
        const lat = parseFloat(result.lat)
        const lng = parseFloat(result.lon)
        
        // Move o mapa para a localização
        this.map.setView([lat, lng], 16)
        
        // Adiciona um marcador temporário
        const marker = L.marker([lat, lng])
          .addTo(this.map)
          .bindPopup(`
            <div>
              <strong>${cepData.localidade} - ${cepData.uf}</strong><br>
              ${cepData.logradouro}<br>
              ${cepData.bairro}<br>
              CEP: ${cepData.cep}
            </div>
          `)
          .openPopup()
        
        // Remove o marcador após 5 segundos
        setTimeout(() => {
          this.map.removeLayer(marker)
        }, 5000)
        
        this.showSearchSuccess(`Localização encontrada: ${cepData.localidade} - ${cepData.uf}`)
      })
      .catch(error => {
        this.showSearchError('Não foi possível localizar o endereço')
      })
  }

  /**
   * Mostra mensagem de sucesso na busca
   */
  showSearchSuccess(message) {
    this.cepInputTarget.style.borderColor = '#28a745'
    this.cepInputTarget.title = message
    
    setTimeout(() => {
      this.cepInputTarget.style.borderColor = ''
      this.cepInputTarget.title = ''
    }, 3000)
  }

  /**
   * Mostra mensagem de erro na busca
   */
  showSearchError(message) {
    this.cepInputTarget.style.borderColor = '#dc3545'
    this.cepInputTarget.title = message
    
    setTimeout(() => {
      this.cepInputTarget.style.borderColor = ''
      this.cepInputTarget.title = ''
    }, 3000)
  }

  /**
   * Action: Obter localização atual do usuário
   */
  getCurrentLocation() {
    if (!navigator.geolocation) {
      alert('Geolocalização não é suportada por este navegador.')
      return
    }

    this.locationButtonTarget.disabled = true
    this.locationButtonTarget.textContent = 'Localizando...'

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude
        const lng = position.coords.longitude
        
        // Move o mapa para a localização atual
        this.map.setView([lat, lng], 15)
        
        // Adiciona um marcador temporário
        const marker = L.marker([lat, lng])
          .addTo(this.map)
          .bindPopup('Sua localização atual')
          .openPopup()
        
        // Remove o marcador após 3 segundos
        setTimeout(() => {
          this.map.removeLayer(marker)
        }, 3000)
        
        this.locationButtonTarget.disabled = false
        this.locationButtonTarget.textContent = 'Localização'
      },
      (error) => {
        alert('Não foi possível obter sua localização.')
        
        this.locationButtonTarget.disabled = false
        this.locationButtonTarget.textContent = 'Localização'
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    )
  }

  /**
   * Adiciona uma feature ao mapa
   */
  addFeatureToMap(feature) {
    let layer
    
    switch (feature.geometry.type) {
      case 'Point':
        const coords = feature.geometry.coordinates
        
        // Determina qual ícone usar baseado nas propriedades do marcador
        let icon
        if (feature.properties?.markerType === 'health') {
          icon = this.createHealthIcon()
        } else if (feature.properties?.markerType === 'hydration') {
          icon = this.createHydrationIcon()
        } else if (feature.properties?.markerType === 'start') {
          // Usa a cor baseada apenas no tipo de rota
          const routeType = feature.properties?.routeType || 'main';
          const color = this.routeColors[routeType];
          icon = this.createCustomPolylineIcon(color)
        } else if (feature.properties?.markerType === 'end') {
          // Usa a cor baseada apenas no tipo de rota
          const routeType = feature.properties?.routeType || 'main';
          const color = this.routeColors[routeType];
          icon = this.createFinishLineIcon(color)
        } else {
          icon = new L.Icon.Default({
            shadowUrl: null,
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            className: 'leaflet-marker-icon-no-border'
          })
        }
        
        layer = L.marker([coords[1], coords[0]], { icon })
        break
      
      case 'LineString':
        // Para LineString, apenas cria a polyline (os marcadores customizados serão criados separadamente)
        const coordinates = feature.geometry.coordinates
        if (coordinates.length >= 2) {
          // Determina a cor baseada apenas no tipo de rota
          const routeType = feature.properties?.routeType || 'main'
          const color = this.routeColors[routeType] || this.routeColors.main
          
          // Cria a polyline com a cor correta
          const polyline = L.polyline(
            coordinates.map(coord => [coord[1], coord[0]]),
            {
              color: color,
              weight: 6,
              opacity: 0.8
            }
          )
          this.drawnItems.addLayer(polyline)
          
          // Adiciona propriedades da feature à polyline
          polyline.feature = feature
          
          // Adiciona tooltip se houver título
          if (feature.properties?.title) {
            polyline.bindTooltip(feature.properties.title)
          }
          
          // Cria marcadores customizados de largada e chegada para esta polyline
          // (cada polyline terá seus próprios marcadores)
          this.createRouteMarkersForPolyline(polyline)
        }
        return // Retorna aqui pois já adicionou as layers
        
      default:
        return
    }
    
    // Adiciona propriedades e tooltip
    layer.feature = feature
    
    if (feature.properties?.title) {
      layer.bindTooltip(feature.properties.title || 'Sem título')
    }
    
    // Adiciona ao drawnItems
    this.drawnItems.addLayer(layer)
  }
} 